import { useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const useVoucher = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const resolveVoucher = async (voucherCode) => {
    const responseData = await sendRequest({
      url: `/api/v1/voucher/resolve`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body: { voucherCode },
    });
    return responseData;
  };

  const checkVoucher = async (id, pin = undefined) => {
    const body = {};
    if (typeof pin === 'string' && pin.trim().length > 0) {
      body.pin = pin.trim();
    }
    const responseData = await sendRequest({
      url: `/api/v1/voucher/${id}/check`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body,
    });
    return responseData;
  };

  const redeemVoucher = async (
    id,
    bodyData,
    toastMessage = null,
    onStored = null,
    displayInfo = null,
    onSuccess = null,
    successToastMessage = null
  ) => {
    const body = { ...(bodyData || {}) };
    if (typeof body.pin === 'string' && body.pin.trim().length === 0) {
      delete body.pin;
    }
    const responseData = await sendRequest(
      {
        url: `/api/v1/voucher/${id}/redeem`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body,
      },
      toastMessage,
      onStored,
      displayInfo,
      onSuccess,
      successToastMessage
    );
    return responseData;
  };

  const getAllVouchers = async (
    searchQuery = '',
    page = 1,
    limit = 20,
    selectKeys = [],
    onlyMine = false,
    filters = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const params = {};
        if (searchQuery) params.searchQuery = searchQuery;
        if (page) params.page = page;
        if (limit) params.limit = limit;
        if (selectKeys.length > 0) params.selectKeys = selectKeys.join(',');
        if (onlyMine) params.onlyMine = 'true';
        if (filters) params.filters = filters;
        const responseData = await sendRequest({
          url: '/api/v1/voucher',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params,
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getVoucherById = async voucherId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/voucher/${voucherId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Fetch a voucher's redemption history by code. Gated server-side by the dedicated
  // 'voucher:special:redemptionHistory' right; works for product and value vouchers.
  const getVoucherRedemptionHistory = async voucherCode => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/voucher/redemption-history`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { voucherCode },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getPaymentById = async paymentId => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/payment/${paymentId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const createVoucher = async (
    voucherData,
    toastMessage = null,
    onStored = null,
    displayInfo = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest(
          {
            url: '/api/v1/voucher',
            method: 'POST',
            headers: authCtx.getAuthHeaders(),
            body: voucherData,
          },
          toastMessage,
          onStored,
          displayInfo
        );
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getVoucherDownloadURL = async (
    voucherId,
    toastMessage = null,
    onStored = null,
    displayInfo = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest(
          {
            url: `/api/v1/voucher/${voucherId}/download`,
            method: 'GET',
            headers: authCtx.getAuthHeaders(),
          },
          toastMessage,
          onStored,
          displayInfo
        );
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getVouchersZipURL = async (
    voucherIds,
    toastMessage = null,
    onStored = null,
    displayInfo = null
  ) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest(
          {
            url: '/api/v1/voucher/batch/download',
            method: 'POST',
            headers: authCtx.getAuthHeaders(),
            body: { voucherIds },
          },
          toastMessage,
          onStored,
          displayInfo
        );
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getVoucherMonthlyReport = async (year, month) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/voucher/report/monthly',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { year, month },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const getVoucherStatistics = async (
    startDate,
    endDate,
    voucherProductId = null,
    toastMessage = null,
    onStored = null,
    displayInfo = null
  ) => {
    const params = { startDate, endDate };
    if (voucherProductId) {
      params.voucherProductId = voucherProductId;
    }

    return sendRequest(
      {
        url: '/api/v1/voucher/statistics',
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        params,
      },
      toastMessage,
      onStored,
      displayInfo
    );
  };

  const downloadVoucherStatisticsReport = async (
    startDate,
    endDate,
    voucherProductId = null,
    branchId = null
  ) => {
    const params = { startDate, endDate };
    if (voucherProductId) {
      params.voucherProductId = voucherProductId;
    }
    if (branchId) {
      params.branchId = branchId;
    }

    return sendRequest({
      url: '/api/v1/voucher/statistics/report',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
      params,
      returnBlob: true,
    });
  };

  const cancelVoucherById = async (voucherId, reason = null) => {
    return sendRequest({
      url: `/api/v1/voucher/${voucherId}/cancel`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body: reason ? { reason } : {},
    });
  };

  // Cancel several vouchers in one request. The backend processes each voucher on
  // its own and reports the per-voucher outcome, so vouchers that cannot be
  // cancelled are skipped instead of failing the whole run.
  const cancelVouchersBatch = async (voucherIds, reason = null) => {
    return sendRequest({
      url: '/api/v1/voucher/batch/cancel',
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body: reason ? { voucherIds, reason } : { voucherIds },
    });
  };

  const sellOfflineVoucher = async voucherData => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: '/api/v1/voucher/sell-offline',
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: voucherData,
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  const sendVoucherToEmail = async (voucherId, email) => {
    return new Promise(async (resolve, reject) => {
      try {
        const responseData = await sendRequest({
          url: `/api/v1/voucher/${voucherId}/send-email`,
          method: 'POST',
          headers: authCtx.getAuthHeaders(),
          body: { email },
        });
        resolve(responseData);
      } catch (error) {
        reject(error);
      }
    });
  };

  // Resend the voucher PDF to the email address already stored on the voucher
  const resendVoucherEmail = async voucherId => {
    return sendRequest({
      url: `/api/v1/voucher/${voucherId}/resend-email`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body: {},
    });
  };

  // Update the buyer email address stored on the voucher
  const updateVoucherBuyerEmail = async (voucherId, email) => {
    return sendRequest({
      url: `/api/v1/voucher/${voucherId}/buyer-email`,
      method: 'PATCH',
      headers: authCtx.getAuthHeaders(),
      body: { email },
    });
  };

  const issueGiftedBalanceVoucher = async voucherData => {
    return sendRequest({
      url: '/api/v1/voucher/gifted-balance/issue',
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body: voucherData,
    });
  };

  const getGiftedBalanceRedeemLocations = async () => {
    return sendRequest({
      url: '/api/v1/voucher/gifted-balance/redeem-locations',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });
  };

  const getVoucherBranchEntertainments = async () => {
    return sendRequest({
      url: '/api/v1/voucher/branch-entertainments',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });
  };

  const getGiftedBalanceVoucherBudget = async ({ branchId, year, month }) => {
    return sendRequest({
      url: '/api/v1/voucher/gifted-balance/budget',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
      params: { branchId, year, month },
    });
  };

  const getGiftedBalanceVoucherRequests = async (status = '') => {
    const params = {};
    if (status) params.status = status;
    return sendRequest({
      url: '/api/v1/voucher/gifted-balance/requests',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
      params,
    });
  };

  // The approvals list carries two kinds of request: gifted balance vouchers and
  // complaint answers parked for a courtesy voucher. They are decided through
  // different endpoints — approving a parked answer also sends it to the
  // customer — so the caller passes the kind the row came with.
  const approveGiftedBalanceVoucherRequest = async (requestId, kind = 'giftedBalance') => {
    if (kind === 'feedbackVoucher') {
      return sendRequest({
        url: `/api/v1/experienceFeedback/voucherApprovals/${requestId}/approve`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { comment: null },
      });
    }
    return sendRequest({
      url: `/api/v1/voucher/gifted-balance/requests/${requestId}/approve`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body: {},
    });
  };

  const rejectGiftedBalanceVoucherRequest = async (
    requestId,
    reason = '',
    kind = 'giftedBalance'
  ) => {
    if (kind === 'feedbackVoucher') {
      return sendRequest({
        url: `/api/v1/experienceFeedback/voucherApprovals/${requestId}/reject`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { comment: reason || null },
      });
    }
    const body = {};
    if (reason) body.reason = reason;
    return sendRequest({
      url: `/api/v1/voucher/gifted-balance/requests/${requestId}/reject`,
      method: 'POST',
      headers: authCtx.getAuthHeaders(),
      body,
    });
  };

  return {
    isLoading,
    error,
    resolveVoucher,
    checkVoucher,
    redeemVoucher,
    getAllVouchers,
    getVoucherById,
    getVoucherRedemptionHistory,
    getPaymentById,
    createVoucher,
    getVouchersZipURL,
    getVoucherDownloadURL,
    getVoucherMonthlyReport,
    getVoucherStatistics,
    downloadVoucherStatisticsReport,
    cancelVoucherById,
    cancelVouchersBatch,
    sellOfflineVoucher,
    sendVoucherToEmail,
    resendVoucherEmail,
    updateVoucherBuyerEmail,
    issueGiftedBalanceVoucher,
    getGiftedBalanceRedeemLocations,
    getVoucherBranchEntertainments,
    getGiftedBalanceVoucherBudget,
    getGiftedBalanceVoucherRequests,
    approveGiftedBalanceVoucherRequest,
    rejectGiftedBalanceVoucherRequest,
  };
};

export default useVoucher;
